/**
 * Sync lifecycle: exclusive start/stop, recovery, restart, and first-timeline waits.
 */

import {
  HARD_FAILURE_COOLDOWN_MS,
  MATRIX_LIST_SYNC_LIMIT,
  MATRIX_POST_SYNC_HYDRATION_LIMIT,
  MATRIX_SYNC_RECOVERY_CHAIN_MAX,
  RECOVERY_ATTEMPTS_RESET_AFTER_STABLE_MS,
  STOP_CLIENT_SETTLE_MS,
  SYNC_POLL_TIMEOUT_MS,
  TIMELINE_WAIT_POLL_MS,
  WAIT_INITIAL_TIMELINE_MS,
} from './matrixConstants';
import { captureChatException, captureChatMessage, addBreadcrumb } from 'shared/sentry';
import {
  ensureCallEventHandler,
  hasInProgressMatrixCall,
  isHealthySyncState,
  isAbortError,
  isSameMatrixClient,
} from './matrixUtils';
import { schedulePostSyncTimelineHydration } from './matrixTimeline';
import { isMatrixSyncStale } from './matrixTabResume';

/**
 * Clear `since` so the next `startClient()` issues a limited initial `/sync`.
 * The SDK reads `store.getSyncToken()` as the `/sync` `since` query param.
 * Must keep the same MatrixClient — recreating it breaks chat + calls.
 * @param {import('matrix-js-sdk').MatrixClient} client
 */
async function prepareClientForFreshInitialSync(client) {
  if (!client) return;
  const store = client.store;
  if (!store) return;

  try {
    if (typeof store.deleteAllData === 'function') {
      await store.deleteAllData();
    } else if (typeof store.setSyncToken === 'function') {
      store.setSyncToken(null);
    }
  } catch (e) {
    if (!isAbortError(e)) {
      captureChatException(e, { tags: { matrix: 'sync', action: 'prepare-fresh-initial' } });
    }
  }

  try {
    client.__jhVoipHandlersStarted = false;
  } catch (_) {}
}

function mergeRestartOpts(prev, next = {}) {
  if (!prev) return { ...next };
  const nextMin = next.minIntervalMs;
  const prevMin = prev.minIntervalMs;
  const minIntervalMs =
    nextMin == null ? prevMin : prevMin == null ? nextMin : Math.min(prevMin, nextMin);
  return {
    ...prev,
    ...next,
    force: Boolean(prev.force || next.force),
    freshInitialSync: Boolean(prev.freshInitialSync || next.freshInitialSync),
    minIntervalMs,
    initialSyncLimit: next.initialSyncLimit ?? prev.initialSyncLimit ?? MATRIX_LIST_SYNC_LIMIT,
    reason: next.reason || prev.reason,
  };
}

function deferForActiveCall(reason, freshInitialSync) {
  addBreadcrumb({
    category: 'matrix.sync',
    message: 'defer sync restart during active call',
    level: 'info',
    data: { reason: reason || 'unknown', freshInitialSync: Boolean(freshInitialSync) },
  });
}

/**
 * @param {import('./matrixUtils').MatrixService} service
 */
export function scheduleRecoveryAttemptsReset(service) {
  if (service._recoveryStableTimer) clearTimeout(service._recoveryStableTimer);
  service._recoveryStableTimer = setTimeout(() => {
    service._recoveryStableTimer = null;
    service.recoveryAttempts = 0;
  }, RECOVERY_ATTEMPTS_RESET_AFTER_STABLE_MS);
}

/**
 * Run stop/start/startClient work exclusively (avoids races with initialize()).
 * @param {import('./matrixUtils').MatrixService} service
 * @param {() => Promise<void>} task
 * @returns {Promise<void>}
 */
export function runExclusiveClientLifecycle(service, task) {
  const run = () => Promise.resolve().then(task);
  service._clientLifecycleOp = service._clientLifecycleOp.then(run, run).catch(() => {});
  return service._clientLifecycleOp;
}

/**
 * Exclusive sync lifecycle: optional stop + settle + startClient (matrix-js-sdk).
 * @param {import('./matrixUtils').MatrixService} service
 * @param {Object} startOpts
 * @param {{ stopFirst?: boolean, prepareFreshInitialSync?: boolean }} [options]
 * @returns {Promise<boolean>} whether startClient ran
 */
export async function exclusiveStartMatrixClient(
  service,
  startOpts,
  { stopFirst = false, prepareFreshInitialSync = false } = {},
) {
  let didStart = false;
  await runExclusiveClientLifecycle(service, async () => {
    const c = service.matrixClient;
    if (!c) return;
    if (stopFirst) {
      // stopClient() drops callEventHandler (SDK does not recreate it in startClient).
      // Never stop while a 1:1 call is live — signaling and CallEngine would be orphaned.
      if (hasInProgressMatrixCall(c)) {
        service._pendingRestart = mergeRestartOpts(service._pendingRestart, {
          force: true,
          minIntervalMs: 0,
          freshInitialSync: prepareFreshInitialSync,
          initialSyncLimit: startOpts?.initialSyncLimit,
          reason: 'deferred-active-call',
        });
        deferForActiveCall('stopClient', prepareFreshInitialSync);
        return;
      }
      c.stopClient?.();
      await new Promise((r) => setTimeout(r, STOP_CLIENT_SETTLE_MS));
    }
    if (!isSameMatrixClient(service, c)) return;
    if (prepareFreshInitialSync) {
      await prepareClientForFreshInitialSync(c);
      if (!isSameMatrixClient(service, c)) return;
    }
    const started = c.startClient?.(startOpts);
    if (started && typeof started.then === 'function') await started;
    // stopClient() clears callEventHandler; startClient() does NOT recreate it — restore before any sync/Call code runs.
    if (!isSameMatrixClient(service, c)) return;
    try {
      ensureCallEventHandler(c);
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, { tags: { matrix: 'sync', action: 'ensure-call-handler' } });
      }
    }
    didStart = true;
  });
  return didStart;
}

/**
 * Wait until the first /sync has moved the client into a usable state.
 * @param {import('./matrixUtils').MatrixService} service
 * @param {Object} [options]
 */
export async function awaitReadySyncState(service, { timeoutMs = 30000, generation = null } = {}) {
  const client = service.matrixClient;
  if (!client) return;

  const stale = () => generation != null && generation !== service.initGeneration;
  const ready = (state) => state === 'PREPARED' || state === 'SYNCING' || state === 'CATCHUP';

  if (stale()) return;
  if (ready(client.getSyncState?.())) return;

  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        client.removeListener?.('sync', onSync);
      } catch (_) {}
      clearTimeout(timer);
      resolve();
    };

    const onSync = (state) => {
      if (stale()) return finish();
      if (ready(state)) finish();
    };

    const timer = setTimeout(finish, timeoutMs);
    client.on?.('sync', onSync);
  });
}

/**
 * Wait until joined rooms have live-timeline events from the limited /sync, or time out.
 * Used for VoIP replay (timelineReady), not as the inbox gate — that is PREPARED + getRooms().
 * @param {import('./matrixUtils').MatrixService} service
 * @param {Object} [options]
 */
export async function waitForInitialTimeline(
  service,
  { timeoutMs = WAIT_INITIAL_TIMELINE_MS, generation = null } = {},
) {
  const client = service.matrixClient;
  if (!client) return;

  const stale = () => generation != null && generation !== service.initGeneration;

  const timelineHasEvents = () => {
    const rooms = client.getRooms?.() || [];
    const joinedRooms = rooms.filter((room) => {
      try {
        if (!room) return false;
        if (typeof room.getMyMembership === 'function') return room.getMyMembership() === 'join';
        return true;
      } catch (_) {
        return false;
      }
    });

    const roomsToConsider = joinedRooms.length > 0 ? joinedRooms : rooms;
    if (roomsToConsider.length === 0) return false;

    const joinedWithEventsCount = roomsToConsider.reduce((acc, room) => {
      const events = room?.getLiveTimeline?.()?.getEvents?.() || [];
      return acc + (events.length > 0 ? 1 : 0);
    }, 0);

    if (roomsToConsider.length <= 2) return joinedWithEventsCount >= 1;
    return joinedWithEventsCount >= 2;
  };

  if (timelineHasEvents()) return;

  await new Promise((resolve) => {
    let settled = false;
    let pollTimer = null;
    const cleanup = () => {
      try {
        client.removeListener?.('Room.timeline', onTimeline);
        client.removeListener?.('sync', onSync);
      } catch (_) {}
      if (pollTimer) clearInterval(pollTimer);
    };
    const finish = (warnTimeout) => {
      if (settled) return;
      settled = true;
      cleanup();
      clearTimeout(hardTimeout);
      // Soft timeout is expected on slow networks / empty accounts — do not open a Sentry issue.
      if (warnTimeout && !timelineHasEvents()) {
        addBreadcrumb({
          category: 'matrix.sync',
          message: 'initial timeline wait timed out',
          level: 'info',
        });
      }
      resolve();
    };

    const check = () => {
      if (stale()) return finish(false);
      if (timelineHasEvents()) return finish(false);
    };

    const onTimeline = () => check();
    const onSync = () => check();

    client.on?.('Room.timeline', onTimeline);
    client.on?.('sync', onSync);
    pollTimer = setInterval(check, TIMELINE_WAIT_POLL_MS);
    const hardTimeout = setTimeout(() => finish(true), timeoutMs);
  });
}

/**
 * Restart sync (stopClient + startClient) on the same MatrixClient.
 *
 * Concurrent callers merge into one pending restart so tab-resume `freshInitialSync`
 * is not dropped by an in-flight incremental restart.
 * Waiters join the in-flight promise (do not re-enter — that deadlocked stop/start
 * and would interrupt WebRTC if a call started mid-queue).
 *
 * @param {import('./matrixUtils').MatrixService} service
 * @param {Object} [options]
 * @returns {Promise<boolean>}
 */
export async function restartSyncImpl(service, options = {}) {
  if (!service.matrixClient) return false;

  service._pendingRestart = mergeRestartOpts(service._pendingRestart, options);

  if (hasInProgressMatrixCall(service.matrixClient)) {
    deferForActiveCall(options.reason, options.freshInitialSync);
    return false;
  }

  if (service.syncRestartPromise) {
    return service.syncRestartPromise;
  }

  const running = runPendingRestarts(service);
  service.syncRestartPromise = running;
  try {
    return await running;
  } finally {
    if (service.syncRestartPromise === running) {
      service.syncRestartPromise = null;
    }
  }
}

async function runPendingRestarts(service) {
  let result = false;
  for (let i = 0; i < MATRIX_SYNC_RECOVERY_CHAIN_MAX && service._pendingRestart; i += 1) {
    const opts = service._pendingRestart;
    service._pendingRestart = null;
    if (hasInProgressMatrixCall(service.matrixClient)) {
      service._pendingRestart = mergeRestartOpts(service._pendingRestart, opts);
      break;
    }
    result = await runOneSyncRestart(service, opts);
  }
  return result;
}

async function runOneSyncRestart(service, options = {}) {
  let {
    reason = 'unknown',
    initialSyncLimit = MATRIX_LIST_SYNC_LIMIT,
    minIntervalMs = null,
    force = false,
    freshInitialSync = false,
  } = options;

  if (!service.matrixClient) return false;

  if (hasInProgressMatrixCall(service.matrixClient)) {
    service._pendingRestart = mergeRestartOpts(service._pendingRestart, options);
    deferForActiveCall(reason, freshInitialSync);
    return false;
  }

  if (isMatrixSyncStale()) {
    freshInitialSync = true;
    force = true;
    minIntervalMs = 0;
  }

  const now = Date.now();
  if (service.lastHardFailureAt && now - service.lastHardFailureAt < HARD_FAILURE_COOLDOWN_MS) {
    return false;
  }

  if (service.refreshPromise && !force) {
    await service.refreshPromise;
  }
  if (!service.matrixClient) return false;

  if (service.initPromise) {
    try {
      await service.initPromise;
    } catch (_) {}
  }
  if (!service.matrixClient) return false;

  const throttleMs =
    minIntervalMs != null ? minIntervalMs : Math.min(60000, 5000 * Math.max(1, service.recoveryAttempts + 1));

  if (now - service.lastSyncRestartAt < throttleMs) {
    return false;
  }

  const currentState = service.matrixClient.getSyncState?.();
  if (!force && !freshInitialSync && isHealthySyncState(currentState) && service._timelineReady) {
    return false;
  }

  service.lastSyncRestartAt = now;

  const clientAtStart = service.matrixClient;
  const genAtStart = service.initGeneration;
  const gone = () =>
    !isSameMatrixClient(service, clientAtStart) || service.initGeneration !== genAtStart;

  try {
    service.recoveryAttempts += 1;
    if (service.recoveryAttempts > service.maxRecoveryAttempts) {
      captureChatMessage('Matrix recovery limit reached', 'error', { tags: { matrix: 'sync' } });
      service.lastHardFailureAt = Date.now();
      service._emitStateChange({
        type: 'RECOVERY_LIMIT_REACHED',
        reason,
        attempts: service.recoveryAttempts,
      });
      service.reset();
      service._invokeAuthFailure({ reason: 'RECOVERY_LIMIT_REACHED' });
      return false;
    }

    addBreadcrumb({
      category: 'matrix.sync',
      message: `restart sync (${reason})`,
      level: 'info',
      data: { reason, currentState, freshInitialSync },
    });
    service._emitStateChange({
      type: 'SYNC_RESTART',
      reason,
      attempts: service.recoveryAttempts,
      freshInitialSync,
    });

    if (hasInProgressMatrixCall(service.matrixClient)) {
      service._pendingRestart = mergeRestartOpts(service._pendingRestart, {
        ...options,
        reason,
        force,
        freshInitialSync,
        minIntervalMs,
        initialSyncLimit,
      });
      deferForActiveCall(reason, freshInitialSync);
      return false;
    }

    service.emitTimelineReady(false);
    const didStart = await exclusiveStartMatrixClient(
      service,
      {
        initialSyncLimit,
        pollTimeout: SYNC_POLL_TIMEOUT_MS,
        lazyLoadMembers: true,
      },
      { stopFirst: true, prepareFreshInitialSync: freshInitialSync },
    );

    if (!didStart) {
      if (!gone() && hasInProgressMatrixCall(service.matrixClient)) {
        service.emitTimelineReady(true);
      }
      return false;
    }

    if (gone()) return false;
    await awaitReadySyncState(service, { generation: service.initGeneration });

    if (gone()) return false;
    scheduleRecoveryAttemptsReset(service);

    await waitForInitialTimeline(service, {
      timeoutMs: WAIT_INITIAL_TIMELINE_MS,
      generation: service.initGeneration,
    });

    if (gone()) return false;
    service.emitTimelineReady(true);

    try {
      ensureCallEventHandler(service.matrixClient);
    } catch (e) {
      if (!isAbortError(e)) {
        captureChatException(e, { tags: { matrix: 'sync', action: 'ensure-call-handler-restart' } });
      }
    }

    schedulePostSyncTimelineHydration(service, {
      limit: MATRIX_POST_SYNC_HYDRATION_LIMIT,
      generation: service.initGeneration,
      context: freshInitialSync ? 'freshInitialSync' : 'restartSync',
    });
    return true;
  } catch (e) {
    if (isAbortError(e)) return false;
    captureChatException(e, { tags: { matrix: 'sync', action: 'restart-failed' } });
    try {
      await service.reinitialize(null, { preserveRecoveryAttempts: true });
      service.recoveryAttempts = 0;
      return true;
    } catch (reinitErr) {
      captureChatException(reinitErr, { tags: { matrix: 'sync', action: 'reinit-after-restart' } });
      service.lastHardFailureAt = Date.now();
      return false;
    }
  }
}
